/**
 * Escalating reload ladder when Workbox skipWaiting / location.reload() does not
 * unload the document (stuck SW, bfcache, deploy mid-rollout).
 */

import { clearAdminPwaClientState } from './clearAdminPwaClientState.js';

export type AdminPwaRefreshEscalationStep =
  | 'clear_and_reload'
  | 'cache_bust_replace'
  | 'origin_replace';

/** Ordered recovery steps — each is stronger than the last. */
export const ADMIN_PWA_REFRESH_ESCALATION_STEPS: readonly AdminPwaRefreshEscalationStep[] =
  ['clear_and_reload', 'cache_bust_replace', 'origin_replace'];

export const PWA_RECOVERY_QUERY = '_pwa';

export function buildAdminPwaCacheBustUrl(nowMs: number = Date.now()): string {
  const url = new URL(window.location.href);
  url.searchParams.set(PWA_RECOVERY_QUERY, String(nowMs));
  return url.toString();
}

/** Step 3 — preserve tenant deep link (pathname + search minus _pwa). */
export function buildAdminPwaOriginRecoveryUrl(nowMs: number = Date.now()): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(PWA_RECOVERY_QUERY);
  url.searchParams.set(PWA_RECOVERY_QUERY, String(nowMs));
  return url.toString();
}

/** Strip recovery query param after successful boot — no reload loop. */
export function stripAdminPwaRecoveryQueryFromUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has(PWA_RECOVERY_QUERY)) {
    return false;
  }
  url.searchParams.delete(PWA_RECOVERY_QUERY);
  const nextSearch = url.searchParams.toString();
  const nextUrl =
    url.pathname + (nextSearch.length > 0 ? `?${nextSearch}` : '') + url.hash;
  window.history.replaceState(null, '', nextUrl);
  return true;
}

export interface AdminPwaRefreshEscalationDeps {
  readonly clearClientState: () => Promise<void>;
  readonly reload: () => void;
  readonly replace: (url: string) => void;
  readonly nowMs?: number;
}

export async function runAdminPwaRefreshEscalationStep(
  step: AdminPwaRefreshEscalationStep,
  deps: AdminPwaRefreshEscalationDeps,
): Promise<void> {
  const nowMs = deps.nowMs ?? Date.now();
  await deps.clearClientState();
  if (step === 'clear_and_reload') {
    deps.reload();
    return;
  }
  const url =
    step === 'cache_bust_replace'
      ? buildAdminPwaCacheBustUrl(nowMs)
      : buildAdminPwaOriginRecoveryUrl(nowMs);
  deps.replace(url);
}

/** Live browser deps — used by AdminPwaLifecycle and peer-tab reload. */
export async function runLiveAdminPwaRefreshEscalationStep(
  step: AdminPwaRefreshEscalationStep,
): Promise<void> {
  return runAdminPwaRefreshEscalationStep(step, {
    clearClientState: async () => {
      const release = await clearAdminPwaClientState();
      // When controller persists after bounded wait, navigation still proceeds;
      // AdminPwaLifecycle watchdog escalates to cache_bust_replace / origin_replace.
      if (!release.released) {
        return;
      }
    },
    reload: () => {
      window.location.reload();
    },
    replace: (url) => {
      window.location.replace(url);
    },
  });
}

/** Peer-tab / BroadcastChannel path — always clear SW before navigation. */
export async function runAdminPwaPeerTabReload(): Promise<void> {
  await runLiveAdminPwaRefreshEscalationStep('clear_and_reload');
}
